import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LabelList,
  LineChart,
  Line,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import {
  BatteryMedium,
  Watch,
  Link2,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  PersonStanding,
  Armchair,
} from "lucide-react";
import {
  usePacSanaSummary,
  usePacSanaSync,
  usePacSanaDayActivity,
} from "../../../hooks/usePacSana";
import {
  VISIBLE_READINGS,
  BAND_COLORS,
  resolveReading,
  readingPoints,
  readingEvents,
  readingEventsTotal,
  normalizeReading,
} from "./pacsanaReadings";
import PacSanaDayDial, {
  DIAL_COLORS,
  extractDayActivity,
  dayActivityTotals,
} from "./PacSanaDayDial";
import { sampleSleepHistory } from "./pacSanaMockData";
import "./PacSanaSection.css";

const fmtNum = (n, decimals = 0) =>
  n === null || n === undefined ? "—" : Number(n).toFixed(decimals);

// "2026-07-25" → "07/25", matching how PacSana's own dashboard labels days.
const fmtDay = (iso) => (iso ? iso.slice(5).replace("-", "/") : "");

// "2026-08-02" → "08.02.2026", the format on PacSana's day picker.
const fmtDayPicker = (iso) => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  return `${month}.${day}.${year}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// "just now" / "12 min ago" / "3 h ago" / a date once it is older than a day.
const fmtSince = (iso) => {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return fmtDate(iso);
};

const fmtWait = (seconds) => {
  const mins = Math.ceil((Number(seconds) || 0) / 60);
  return mins > 1 ? `${mins} minutes` : "a minute";
};

// PacSana's historical endpoint answers at most 31 days per call, so the picker
// enforces the same ceiling rather than letting the request fail.
const MAX_RANGE_DAYS = 31;
const DEFAULT_RANGE_DAYS = 14;

// The readings that plot as a daily series — everything the shared day axis and
// the normaliser apply to. The clock and the event list are neither.
const SERIES_READINGS = VISIBLE_READINGS.filter((reading) =>
  ["bar", "line"].includes(reading.chart),
);

const DAY_MS = 24 * 60 * 60 * 1000;

// Dates are handled as plain yyyy-MM-dd strings on a UTC clock, so a range never
// slips a day for a user whose timezone is behind or ahead of the server's.
const isoOf = (ms) => new Date(ms).toISOString().slice(0, 10);
const msOf = (iso) => Date.parse(`${iso}T00:00:00Z`);
const shiftIso = (iso, days) => isoOf(msOf(iso) + days * DAY_MS);
const spanOf = (from, to) => Math.round((msOf(to) - msOf(from)) / DAY_MS) + 1;

const defaultRange = () => {
  const to = isoOf(Date.now());
  return { from: shiftIso(to, -(DEFAULT_RANGE_DAYS - 1)), to };
};

/* Activity status carries an icon, as it does in PacSana's own status column:
   a walking figure for active, a chair for resting, and the same walking figure
   greyed back when the bracelet is out of range. The label stays alongside it —
   the icon is a second reading of the status, never the only one. */
const ACTIVITY_STATUS = {
  active: { Icon: PersonStanding, tone: "strong" },
  walking: { Icon: PersonStanding, tone: "strong" },
  moving: { Icon: PersonStanding, tone: "strong" },
  resting: { Icon: Armchair, tone: "strong" },
  rest: { Icon: Armchair, tone: "strong" },
  sitting: { Icon: Armchair, tone: "strong" },
  inactive: { Icon: Armchair, tone: "strong" },
  outofrange: { Icon: PersonStanding, tone: "faint" },
  offline: { Icon: PersonStanding, tone: "faint" },
  unknown: { Icon: PersonStanding, tone: "faint" },
};

const StatusChip = ({ status }) => {
  const meta = ACTIVITY_STATUS[String(status).toLowerCase().replace(/[^a-z]/g, "")];
  return (
    <span
      className={`pacsana-chip ${meta ? `pacsana-status-${meta.tone}` : ""}`}
      aria-label={`Activity status: ${status}`}
    >
      {meta ? <meta.Icon size={17} strokeWidth={meta.tone === "strong" ? 2.4 : 2} /> : null}
      {/* PacSana capitalises some statuses and not others ("Active" beside
          "offline"), so the chip row lowercases them all to read as one set.
          The aria-label above keeps the value as sent. */}
      {String(status).toLowerCase()}
    </span>
  );
};

// A bracelet that isn't being worn reports nothing, so it is called out in red
// rather than sharing the green every other chip icon takes.
const NOT_WORN = ["unworn", "notworn", "off", "offwrist", "removed", "notwearing"];

const WearChip = ({ status }) => {
  const word = String(status).toLowerCase().replace(/[^a-z]/g, "");
  const off = NOT_WORN.some((candidate) => word.startsWith(candidate));
  return (
    <span
      className={`pacsana-chip ${off ? "pacsana-chip-alert" : ""}`}
      aria-label={`Bracelet: ${status}`}
    >
      <Watch size={14} />
      {status}
    </span>
  );
};

const pickReading = (data, config) =>
  resolveReading(data?.readings, config) || resolveReading(data, config);

// PacSana stamps events with no offset — "2026-08-06T05:06:00" — because that
// is already the wearer's wall clock, the same clock it writes into the event
// text. So the components are read straight off the string: parsing it into a
// Date reads it as the *browser's* zone and then reporting it in UTC shifts it
// again, which printed "9:06 AM" beside a message that said 5:06 AM.
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

// "08.05.2026 2:32 AM", as PacSana's own event table prints it.
const fmtStamp = (iso) => {
  if (!iso) return "—";

  const pad = (n) => String(n).padStart(2, "0");
  const render = (year, month, day, hour, minute) =>
    `${pad(month)}.${pad(day)}.${year} ${hour % 12 || 12}:${pad(minute)} ${hour < 12 ? "AM" : "PM"}`;

  const wall = WALL_CLOCK.exec(String(iso));
  if (wall) {
    const [, year, month, day, hour, minute] = wall;
    return render(year, Number(month), Number(day), Number(hour), Number(minute));
  }

  // A stamp that does carry an offset is a real instant; UTC keeps it stable
  // whoever is reading the panel.
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return String(iso);
  return render(
    at.getUTCFullYear(),
    at.getUTCMonth() + 1,
    at.getUTCDate(),
    at.getUTCHours(),
    at.getUTCMinutes(),
  );
};

// PacSana leaves an event "New" until someone deals with it; a closed one is
// history, and its whole row steps back.
const eventTone = (status) => {
  const word = String(status || "").toLowerCase().replace(/[^a-z]/g, "");
  if (word.startsWith("new") || word.startsWith("open") || word.startsWith("active")) return "new";
  if (word.startsWith("closed") || word.startsWith("resolved") || word.startsWith("done")) return "closed";
  return "other";
};

// "07/27, 07/28 and 08/02" — the way PacSana writes its missing-day lists.
const listJoin = (items) => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

// "Sync now" — asks the backend to pull fresh PacSana data for this client's
// agency. The pull runs in the background, so the button only reports that it
// started; the summary query polls until the new readings land.
const SyncControl = ({ sync, mutation }) => {
  const running = !!sync?.inProgress || mutation.isPending;
  const failure = mutation.error?.response?.data;

  let note = null;
  if (failure?.status === "cooldown") {
    note = `Synced recently — try again in ${fmtWait(failure.cooldownSeconds)}.`;
  } else if (failure) {
    note = failure.message || "Couldn't start the sync.";
  } else if (running) {
    note = "Fetching from PacSana — this can take a minute.";
  } else if (sync?.lastSyncAt) {
    note = `Last synced ${fmtSince(sync.lastSyncAt)}`;
  }

  return (
    <div className="pacsana-sync">
      <button
        type="button"
        className="pacsana-sync-btn"
        onClick={() => mutation.mutate()}
        disabled={running}
      >
        <RefreshCw size={13} className={running ? "pacsana-spin" : undefined} />
        {running ? "Syncing…" : "Sync now"}
      </button>
      {note ? (
        <span className={`pacsana-sync-note ${failure ? "pacsana-sync-note-error" : ""}`}>
          {note}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Date-range filter for the whole panel: a pill showing the current range, a
 * popover with the two dates, and the button that fetches them.
 *
 * The 31-day ceiling is PacSana's, and it is enforced here so an over-long range
 * is refused with a reason rather than failing at the request.
 */
const RangeFilter = ({ range, onApply, busy }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);

  const today = isoOf(Date.now());
  const span = draft.from && draft.to ? spanOf(draft.from, draft.to) : 0;

  let error = null;
  if (!draft.from || !draft.to) error = "Pick both dates.";
  else if (span < 1) error = "The end date is before the start date.";
  else if (span > MAX_RANGE_DAYS) {
    error = `PacSana serves ${MAX_RANGE_DAYS} days at most — that range is ${span}.`;
  }

  const apply = () => {
    if (error) return;
    setOpen(false);
    onApply(draft);
  };

  return (
    <div className="pacsana-range">
      <div className="pacsana-range-field">
        <button
          type="button"
          className="pacsana-range-pill"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          {fmtDayPicker(range.from)} – {fmtDayPicker(range.to)}
          <ChevronDown size={14} />
        </button>

        {open ? (
          <div className="pacsana-range-pop">
            <label>
              <span>From</span>
              <input
                type="date"
                value={draft.from}
                max={draft.to || today}
                onChange={(e) => setDraft((prev) => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={draft.to}
                min={draft.from}
                max={today}
                onChange={(e) => setDraft((prev) => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <p className={`pacsana-range-note ${error ? "pacsana-range-note-error" : ""}`}>
              {error || `${span} day${span === 1 ? "" : "s"} selected`}
            </p>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className="pacsana-range-apply"
        onClick={apply}
        disabled={!!error || busy}
      >
        {busy ? "Loading…" : "Show data"}
      </button>
    </div>
  );
};

const ChartTooltip = ({ active, payload, unit, decimals }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  if (point.value === null) return null;
  return (
    <div className="pacsana-tip">
      <div className="pacsana-tip-day">{fmtDay(point.date)}</div>
      <div className="pacsana-tip-value">
        {fmtNum(point.value, decimals)} <span>{unit}</span>
      </div>
      {point.description ? <div className="pacsana-tip-sub">{point.description}</div> : null}
    </div>
  );
};

// The pill sitting on a threshold line ("WHO Recommendation", "Very Slow").
// Recharts clones this with the line's viewBox, so it can be right-aligned to
// the plot the way PacSana labels its own bands.
const BandLabel = ({ viewBox, text, color }) => {
  if (!viewBox) return null;
  const width = text.length * 6.1 + 14;
  const x = viewBox.x + viewBox.width - width - 2;
  return (
    <g transform={`translate(${x}, ${viewBox.y - 9})`}>
      <rect width={width} height={18} rx={4} fill={color} />
      <text x={width / 2} y={12.5} textAnchor="middle" className="pacsana-band-text">
        {text}
      </text>
    </g>
  );
};

// The two headline figures for the selected reading, sized like the CareLink360
// tiles so the two device panels read the same way.
const HeadlineTiles = ({ reading, decimals, spanDays }) => (
  <div className="pacsana-tiles">
    <div className="pacsana-tile">
      <div className="pacsana-tile-value">
        {fmtNum(reading.latest?.value, decimals)}
        {reading.unit ? <span className="pacsana-tile-unit">{reading.unit}</span> : null}
      </div>
      <div className="pacsana-tile-label">Latest reading</div>
    </div>
    <div className="pacsana-tile">
      <div className="pacsana-tile-value">
        {fmtNum(reading.average, decimals)}
        {reading.unit ? <span className="pacsana-tile-unit">{reading.unit}</span> : null}
      </div>
      <div className="pacsana-tile-label">Daily average · last {spanDays} days</div>
    </div>
  </div>
);

/**
 * PacSana's guidance block: what the chart says, why it matters and what to do
 * about it. Open by default — the reading means little to most readers without
 * it — and collapsible for anyone who only wants the chart.
 */
const DescriptionPanel = ({ summary, config }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className={`pacsana-desc ${open ? "open" : ""}`}>
      <div className="pacsana-desc-head">
        <span className="pacsana-desc-title">Description</span>
        <button
          type="button"
          className="pacsana-desc-toggle"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? "Hide" : "Show"}
          <ChevronDown size={14} className="pacsana-desc-chevron" />
        </button>
      </div>

      {open ? (
        <div className="pacsana-desc-body">
          <p className="pacsana-desc-summary">{summary}</p>

          {config.why?.length ? (
            <>
              <h4 className="pacsana-desc-h">Why it matters</h4>
              {config.why.map((text) => (
                <p key={text}>{text}</p>
              ))}
            </>
          ) : null}

          {config.what?.length || config.whatList?.length ? (
            <>
              <h4 className="pacsana-desc-h">What to do</h4>
              {config.what?.map((text) => (
                <p key={text}>{text}</p>
              ))}
              {config.whatList?.length ? (
                <ol className="pacsana-desc-list">
                  {config.whatList.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ol>
              ) : null}
              {config.whatAfter?.map((text) => (
                <p key={text}>{text}</p>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// The sentence above the guidance: the range, the average, PacSana's verdict on
// that average, and which days the bracelet reported nothing for.
const buildSummary = (reading, config) => {
  const points = reading?.points || [];
  const recorded = points.filter((p) => p.value !== null && p.value !== undefined);
  const label = config.label;
  const unit = reading?.unit ? ` ${reading.unit}` : "";
  const { decimals } = config;

  if (!recorded.length) {
    return `No ${label.toLowerCase()} recorded over the period.`;
  }

  const values = recorded.map((p) => Number(p.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const average =
    reading.average ?? values.reduce((sum, v) => sum + v, 0) / values.length;

  let text =
    min === max
      ? `${label} was ${fmtNum(min, decimals)}${unit} on the one day recorded`
      : `${label} ranged from ${fmtNum(min, decimals)} to ${fmtNum(max, decimals)}${unit} over the period, averaging ${fmtNum(average, decimals)}`;

  const verdict = config.verdict?.(average);
  text += verdict ? `, ${verdict}.` : ".";

  const missing = points
    .filter((p) => p.value === null || p.value === undefined)
    .map((p) => fmtDay(p.date));
  if (missing.length) {
    text += ` No ${label.toLowerCase()} recorded on ${listJoin(missing)}.`;
  }

  return text;
};

// One reading in full: its two headline figures, then the daily series with
// PacSana's amber/red bands, then the guidance. Only the selected reading
// renders, so each chart gets the panel's full width.
const SeriesPanel = ({ reading, config, spanDays, rangeLabel }) => {
  const { label, chart, decimals, bands } = config;
  const points = reading?.points || [];
  const hasData = (reading?.daysWithData || 0) > 0;

  if (!hasData) {
    return (
      <div className="pacsana-pane-empty">
        <p>No {label.toLowerCase()} readings in this range.</p>
        <span>Try another date range, or sync for the most recent days.</span>
      </div>
    );
  }

  // The bands only guide if they are on screen, so the axis has to reach the
  // highest of them even when every reading sits below it.
  const bandCeiling = Math.max(0, ...(bands || []).map((b) => b.value));
  const axisMax = (dataMax) => Math.ceil(Math.max(dataMax, bandCeiling) * 1.08);

  const axes = (
    <>
      <CartesianGrid vertical={false} />
      <XAxis dataKey="date" tickFormatter={fmtDay} tickLine={false} />
      <YAxis
        allowDecimals={decimals > 0}
        width={44}
        axisLine={false}
        tickLine={false}
        domain={[0, axisMax]}
      />
      <Tooltip content={<ChartTooltip unit={reading.unit} decimals={decimals} />} />
      {(bands || []).map((band) => (
        <ReferenceLine
          key={band.label}
          y={band.value}
          stroke={BAND_COLORS[band.tone]}
          strokeDasharray="3 3"
          label={<BandLabel text={band.label} color={BAND_COLORS[band.tone]} />}
        />
      ))}
    </>
  );

  return (
    <div className="pacsana-pane-body">
      <HeadlineTiles reading={reading} decimals={decimals} spanDays={spanDays} />

      <div className="pacsana-chart">
        <div className="pacsana-chart-head">
          <div className="pacsana-chart-title">{label} by day</div>
          <span className="pacsana-chart-hint">{reading.unit}</span>
        </div>

        {/* Grid, axis and mark colours come from CSS so they follow the tab
            accent and the light/dark steps — a var() in an SVG presentation
            attribute doesn't resolve everywhere. Band colours are the exception:
            amber and red mean the same thing on every tab. */}
        <ResponsiveContainer width="100%" height={240}>
          {chart === "bar" ? (
            <BarChart data={points} margin={{ top: 14, right: 6, left: -14, bottom: 0 }}>
              {axes}
              {/* Square-topped bars with the figure printed inside, matching
                  PacSana's dashboard. */}
              <Bar dataKey="value" maxBarSize={48}>
                <LabelList
                  dataKey="value"
                  position="insideBottom"
                  offset={8}
                  className="pacsana-bar-value"
                  formatter={(v) => (v ? fmtNum(v, decimals) : "")}
                />
              </Bar>
            </BarChart>
          ) : (
            <LineChart data={points} margin={{ top: 14, right: 6, left: -14, bottom: 0 }}>
              {axes}
              {/* connectNulls false so a day with no reading shows as a break
                  in the line rather than an invented straight segment. */}
              <Line type="monotone" dataKey="value" strokeWidth={3} dot={{ r: 4 }} connectNulls={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <DescriptionPanel summary={buildSummary(reading, config)} config={config} />

      <div className="pacsana-pane-foot">
        {rangeLabel ? `${rangeLabel} · ` : ""}
        {reading.daysWithData} day{reading.daysWithData === 1 ? "" : "s"} recorded
      </div>
    </div>
  );
};

// Ordered as PacSana's own legend runs: what the person was doing, then where
// they were, then the absence of a reading.
const PAGE_SIZES = [10, 25, 50];

/**
 * Event history, laid out as PacSana's own table: the state it was left in, when
 * it happened, and what it says — closed events stepped back so the ones still
 * wanting attention carry the row.
 *
 * The endpoint pages these, so the footer reports the page and the total rather
 * than implying what is on screen is everything. Whatever arrives is also sliced
 * here, so the same footer holds whether the backend pages or sends the lot.
 */
const EventsPanel = ({ events, total, config, page, pageSize, onPage, onPageSize }) => {
  const paged = events.length > pageSize
    ? events.slice((page - 1) * pageSize, page * pageSize)
    : events;
  const firstRow = paged.length ? (page - 1) * pageSize + 1 : 0;
  const lastRow = (page - 1) * pageSize + paged.length;

  // Without a total from the endpoint, the page itself is the evidence: a full
  // one means there may be more behind it, a short one means this is the end.
  const pageFull = paged.length >= pageSize;
  const known = total ?? (pageFull ? null : lastRow);
  const hasNext = known === null ? pageFull : lastRow < known;

  return (
    <div className="pacsana-pane-body">
      {/* No heading: the tab overhead already says Event history. */}
      <div className="pacsana-chart">
        {paged.length ? (
          <div className="pacsana-events-scroll">
            {/* The table is split in quarters — status, time, then half of it
                for the event text. Widths are inline, on the columns and on the
                header cells both: something else on this page beats the
                stylesheet, and left to it the table hands Time most of the width
                and crushes the message into a corner. */}
            <table className="pacsana-events" style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "50%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" style={{ width: "25%" }}>Status</th>
                  <th scope="col" style={{ width: "25%" }}>Time</th>
                  <th scope="col" style={{ width: "50%" }}>Event</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((event, index) => (
                  <tr
                    key={`${event.at}-${index}`}
                    className={`pacsana-event-${eventTone(event.status)}`}
                  >
                    <td className="pacsana-event-status">{event.status || "—"}</td>
                    <td className="pacsana-event-time">{fmtStamp(event.at)}</td>
                    {/* The rule type isn't given a column: the event text
                        already says what fired, so it rides along as a tooltip. */}
                    <td className="pacsana-event-message" title={event.ruleType || undefined}>
                      {event.message || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="pacsana-pane-empty">
            <p>No events in this range.</p>
            <span>Nothing PacSana raised for this client over these days.</span>
          </div>
        )}

        <div className="pacsana-events-foot">
          <label className="pacsana-events-size">
            Rows per page:
            <select
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>

          <div className="pacsana-events-pager">
            <button
              type="button"
              onClick={() => onPage(page - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => onPage(page + 1)}
              disabled={!hasNext}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <span className="pacsana-events-count">
            Showing {firstRow} to {lastRow}
            {known === null ? "" : ` of ${known} total record${known === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      <DescriptionPanel
        summary={
          known
            ? `${known} event${known === 1 ? "" : "s"} raised over the selected range.`
            : "No events were raised over the selected range."
        }
        config={config}
      />
    </div>
  );
};

const DIAL_LEGEND = [
  { kind: "activity", label: "Activity" },
  { kind: "sleep", label: "Sleep" },
  { kind: "bed", label: "Bed area" },
  { kind: "living", label: "Living area" },
  { kind: "out", label: "Out of range" },
];

// Which of the four ring colours a day actually used — a day with no bed-area
// reading shouldn't advertise a colour that isn't on the dial.
const legendFor = (segments) => {
  const present = new Set(segments.map((s) => s.kind));
  const used = DIAL_LEGEND.filter((entry) => present.has(entry.kind));
  return used.length
    ? used
    : DIAL_LEGEND.filter((entry) => !["bed", "sleep"].includes(entry.kind));
};

const buildDaySummary = (segments, day, loading) => {
  if (loading) return `Fetching the 24-hour readings for ${fmtDay(day)}…`;
  if (!day) {
    return "No days in this range — pick a range PacSana has readings for.";
  }
  if (!segments.length) {
    return `No 24-hour activity recorded for ${fmtDay(day)}. PacSana only fills this in for days the bracelet was detected.`;
  }
  const totals = dayActivityTotals(segments);
  const parts = DIAL_LEGEND.filter(({ kind }) => totals[kind] > 0).map(
    ({ kind, label }) => `${fmtNum(totals[kind], 1)} hours ${label.toLowerCase()}`,
  );
  return `On ${fmtDay(day)}: ${listJoin(parts)}.`;
};

// The 24-hour clock: one day at a time, picked from the days in range.
const DayActivityPanel = ({
  config,
  days,
  byDay,
  activeDay,
  onDayChange,
  rangeLabel,
  loading,
}) => {
  const segments = byDay[activeDay] || [];
  // Only days already fetched can be counted — the rest are one request away.
  const recorded = days.filter((day) => byDay[day]?.length).length;

  return (
    <div className="pacsana-pane-body">
      <div className="pacsana-chart">
        <div className="pacsana-chart-head">
          <div className="pacsana-chart-title">24-Hour Activity</div>
          {days.length ? (
            <label className="pacsana-day-picker">
              <span className="pacsana-sr-only">Day</span>
              <select value={activeDay} onChange={(e) => onDayChange(e.target.value)}>
                {days.map((day) => (
                  <option key={day} value={day}>
                    {fmtDayPicker(day)}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </label>
          ) : null}
        </div>

        <div className={loading ? "pacsana-dial-loading" : undefined}>
          <PacSanaDayDial segments={segments} />
        </div>

        <div className="pacsana-legend">
          {legendFor(segments).map(({ kind, label }) => (
            <span key={kind} className="pacsana-legend-item">
              <span
                className="pacsana-legend-swatch"
                style={{ background: DIAL_COLORS[kind] }}
                aria-hidden="true"
              />
              {label}
            </span>
          ))}
        </div>
      </div>

      <DescriptionPanel
        summary={buildDaySummary(segments, activeDay, loading)}
        config={config}
      />

      <div className="pacsana-pane-foot">
        {rangeLabel ? `${rangeLabel} · ` : ""}
        {loading ? "loading…" : `${recorded} day${recorded === 1 ? "" : "s"} loaded`}
      </div>
    </div>
  );
};

const PacSanaSection = ({ clientId, showSampleSleep = false }) => {
  const [activeTab, setActiveTab] = useState(VISIBLE_READINGS[0].id);
  const [activeDay, setActiveDay] = useState(null);
  // The range shown in the filter, and the one actually fetched — they only
  // differ between picking dates and pressing "Show data".
  const [range, setRange] = useState(defaultRange);
  const [applied, setApplied] = useState(null);
  // Event paging travels with the fetch: PacSana pages events on the same call
  // that carries the daily series.
  const [events, setEvents] = useState({ page: 1, pageSize: PAGE_SIZES[0] });
  const { data, isLoading, isError, isFetching } = usePacSanaSummary(clientId, {
    range: applied,
    events,
  });
  const syncMutation = usePacSanaSync(clientId);

  const activeConfig =
    VISIBLE_READINGS.find((reading) => reading.id === activeTab) || VISIBLE_READINGS[0];

  const applyRange = (next) => {
    setRange(next);
    setApplied(next);
    setActiveDay(null);
    // A new range is a new set of events; page 2 of the old one means nothing.
    setEvents((prev) => ({ ...prev, page: 1 }));
  };

  // Every tab is put on one day axis, built from the range the API answered with
  // — so the charts line up with each other, the day picker offers the same days
  // the charts cover, and a day the API left out reads as a gap rather than
  // closing up.
  const { days, byDay, series } = useMemo(() => {
    const activity = extractDayActivity(data);
    const dates = new Set(activity.days);

    SERIES_READINGS.forEach((config) =>
      readingPoints(pickReading(data, config)).forEach(
        (point) => point?.date && dates.add(String(point.date).slice(0, 10)),
      ),
    );

    // Once a range is asked for, every one of its days belongs on the axis —
    // including days nothing came back for. The 24-hour clock is fetched per
    // day, so those days still have to be offered in its picker.
    if (applied?.from && applied?.to) {
      for (let day = applied.from; day <= applied.to; day = shiftIso(day, 1)) {
        dates.add(day);
      }
    }

    const axis = [...dates].sort();
    const readings = Object.fromEntries(
      SERIES_READINGS.map((config) => [
        config.id,
        normalizeReading(
          // The demo client's sleep history is sample data, in place of
          // whatever the bracelet sent.
          showSampleSleep && config.id === "sleep"
            ? sampleSleepHistory(axis)
            : pickReading(data, config),
          config,
          axis,
        ),
      ]),
    );

    return { days: axis, byDay: activity.byDay, series: readings };
  }, [data, applied, showSampleSleep]);

  // Opens on the most recent day that actually has a dial to draw, the way
  // PacSana's own dashboard does — the newest day in a range is usually still
  // filling in, and landing on it makes a working clock look broken. Falls back
  // to the newest day when nothing is stored yet, so the picker is never empty.
  const daysWithDetail = days.filter((day) => byDay[day]?.length);
  const newestDay = daysWithDetail[daysWithDetail.length - 1] || days[days.length - 1];
  const selectedDay = activeDay && days.includes(activeDay) ? activeDay : newestDay;

  // The clock's day is fetched on its own, only while its tab is open — the
  // summary can't carry a month of minute-level readings.
  const dayQuery = usePacSanaDayActivity(clientId, selectedDay, {
    enabled: activeConfig.chart === "clock",
  });
  const fetchedDay = useMemo(
    () => extractDayActivity(dayQuery.data).byDay,
    [dayQuery.data],
  );
  // The fetched day wins; anything the summary happened to carry is the fallback.
  const dayActivity = { ...byDay, ...fetchedDay };

  // The sync control only appears once a bracelet is linked — there is nothing
  // for an unlinked client to refresh.
  const header = (
    <div className="pacsana-header">
      <div>
        <h2 className="pacsana-title">
          <Watch size={18} strokeWidth={2.5} />
          PacSana
        </h2>
        <p className="pacsana-subtitle">
          Sleep, activity, exercise and gait readings from the client&apos;s PacSana
          bracelet.
        </p>
      </div>
      <div className="pacsana-header-actions">
        <span className="pacsana-source-badge">From PacSana</span>
        {data?.linked ? <SyncControl sync={data.sync} mutation={syncMutation} /> : null}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="pacsana-section">
        {header}
        <div className="pacsana-state">Loading PacSana readings…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="pacsana-section">
        {header}
        <div className="pacsana-state pacsana-state-error">
          Couldn&apos;t load PacSana readings for this client.
        </div>
      </div>
    );
  }

  if (!data || !data.linked) {
    return (
      <div className="pacsana-section">
        {header}
        <div className="pacsana-state pacsana-state-unlinked">
          <Link2 size={18} />
          <div>
            <strong>No PacSana bracelet linked to this client.</strong>
            <span>
              Link one with <code>scripts/linkPacsanaClient.js</code> once the
              roster has synced.
            </span>
          </div>
        </div>
      </div>
    );
  }

  const { device, activityStatus, trendDays } = data;
  const eventList = readingEvents(pickReading(data, activeConfig));
  // Once a range has been asked for, that range is what the labels describe —
  // the backend's own default window no longer applies.
  const spanDays = applied ? spanOf(applied.from, applied.to) : trendDays || days.length;
  // The dates the panel is actually showing: the range asked for, or — before
  // one is asked for — whatever the backend's default window turned out to be.
  const rangeLabel = days.length
    ? `${fmtDayPicker(applied?.from || days[0])} – ${fmtDayPicker(applied?.to || days[days.length - 1])}`
    : null;
  // Latest value for a tab's chip — hours of movement for the 24-hour clock,
  // the reading's own latest figure for everything else.
  const tabFigure = (config) => {
    if (config.chart === "clock") {
      const hours = dayActivityTotals(dayActivity[selectedDay] || []).activity;
      return hours ? fmtNum(hours, 1) : "—";
    }
    const reading = series[config.id];
    return (reading?.daysWithData || 0) > 0
      ? fmtNum(reading.latest?.value, config.decimals)
      : "—";
  };

  return (
    <div className="pacsana-section">
      {header}

      {/* What state the bracelet is in right now, and the range on show. The
          client is named by the page around this panel, so it isn't repeated. */}
      <div className="pacsana-identity">
        <div className="pacsana-chips">
          {device.status ? <WearChip status={device.status} /> : null}
          {device.battery ? (
            <span className="pacsana-chip">
              <BatteryMedium size={14} />
              {/* PacSana types battery as a string and sends words as well as
                  numbers ("okay", "85"), so only a bare number gets a % sign. */}
              {device.battery}
              {/^\d+(\.\d+)?$/.test(String(device.battery).trim()) ? "%" : ""}
            </span>
          ) : null}
          {activityStatus ? <StatusChip status={activityStatus} /> : null}
        </div>

        <RangeFilter range={range} onApply={applyRange} busy={isFetching} />
      </div>

      {/* One tab per PacSana panel. The chip carries the latest value so the
          strip still gives all of them at a glance. */}
      <div className="pacsana-tabs" role="tablist">
        {VISIBLE_READINGS.map((config) => {
          const { id, label, Icon, accent } = config;
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`pacsana-tab pacsana-accent-${accent} ${isActive ? "active" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <span className="pacsana-tab-icon"><Icon size={15} /></span>
              <span className="pacsana-tab-label">{label}</span>
              {config.hideFigure ? null : (
                <span className="pacsana-tab-figure">{tabFigure(config)}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className={`pacsana-pane pacsana-accent-${activeConfig.accent}`}>
        {activeConfig.chart === "events" ? (
          <EventsPanel
            config={activeConfig}
            events={eventList}
            total={readingEventsTotal(pickReading(data, activeConfig))}
            page={events.page}
            pageSize={events.pageSize}
            onPage={(page) => setEvents((prev) => ({ ...prev, page }))}
            onPageSize={(pageSize) => setEvents({ page: 1, pageSize })}
          />
        ) : activeConfig.chart === "clock" ? (
          <DayActivityPanel
            config={activeConfig}
            days={days}
            byDay={dayActivity}
            activeDay={selectedDay}
            onDayChange={setActiveDay}
            rangeLabel={rangeLabel}
            loading={dayQuery.isFetching}
          />
        ) : (
          <SeriesPanel
            config={activeConfig}
            reading={series[activeConfig.id]}
            spanDays={spanDays}
            rangeLabel={rangeLabel}
          />
        )}
      </div>

    </div>
  );
};

export default PacSanaSection;
